import React, { useState, useCallback, useEffect, useRef } from 'react';
import { LazyLoadImage, LazyLoadImageProps } from 'react-lazy-load-image-component';
import { useSelector } from "react-redux";
import { BannersItemModel, SettingModel } from "../../../Redux/Slices/InitialStatePropsModel";
import { Link } from 'react-router-dom';

export function ResponsiveCarousel() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const bannerList: BannersItemModel[] = useSelector((state: any) => state.SettingSlice.banners);
  const categoryList: SettingModel[] = useSelector((state: any) => state.SettingSlice.categoryList);
  const refillCategoryList: SettingModel[] = useSelector((state: any) => state.SettingSlice.refillCategoryList);
  const Image = LazyLoadImage as React.FC<LazyLoadImageProps>;

  // Touch/swipe support
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);

  const moveSlide = useCallback((direction: 'next' | 'prev') => {
    if (isTransitioning) return;

    setIsTransitioning(true);
    setCurrentIndex((prevIndex) => {
      if (direction === 'next') {
        return prevIndex === bannerList?.length - 1 ? 0 : prevIndex + 1;
      } else {
        return prevIndex === 0 ? bannerList?.length - 1 : prevIndex - 1;
      }
    });

    setTimeout(() => setIsTransitioning(false), 400);
  }, [isTransitioning, bannerList?.length]);

  const nextSlide = useCallback(() => moveSlide('next'), [moveSlide]);
  const prevSlide = useCallback(() => moveSlide('prev'), [moveSlide]);

  useEffect(() => {
    const interval = setInterval(nextSlide, 10000);
    return () => clearInterval(interval);
  }, [nextSlide]);

  const getItemStyle = (index: number) => {
    let translateX = (index - currentIndex) * 100;
    if (translateX < -100) translateX += bannerList?.length * 100;
    if (translateX > 100) translateX -= bannerList?.length * 100;

    return {
      transform: `translateX(${translateX}%)`,
      transition: isTransitioning ? 'transform 0.4s ease-in-out' : 'none',
    };
  };

  const getBannerUrl = (item: BannersItemModel) => {
    const category: any = categoryList.find((category: any) => category?._id === item?.categoryId);
    const refillCategory: any = refillCategoryList.find((category: any) => category?._id === item?.categoryId);
    if (category?.categoryName) {
      return `/gift-cards/${category.categoryName}/Gift Cards`;
    } else if (refillCategory?.categoryName) {
      return `/refill/${refillCategory.categoryName}/Phone Refill`;
    } else {
      return `/gift-cards`;
    }
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    const diff = touchStartX.current - touchEndX.current;
    if (Math.abs(diff) > 50) {
      if (diff > 0) nextSlide();
      else prevSlide();
    }
  };

  if (!bannerList || bannerList.length === 0) {
    return (
      <div className="w-full aspect-[5/2] sm:aspect-[3/1] lg:aspect-auto lg:h-[280px] xl:h-[300px] bg-gray-100 dark:bg-dark-foreground rounded-xl animate-pulse" />
    );
  }

  return (
    <div
      className="overflow-hidden rounded-xl relative group"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* Navigation Arrows — visible on hover (desktop) + always visible on mobile */}
      {bannerList.length > 1 && (
        <>
          <button
            onClick={(e) => { e.stopPropagation(); prevSlide(); }}
            className="
              absolute left-2 sm:left-3 top-1/2 -translate-y-1/2 z-10
              bg-black/40 hover:bg-black/60 text-white
              p-1.5 sm:p-2 rounded-full
              transition-all duration-200
              opacity-70 sm:opacity-0 sm:group-hover:opacity-100
              focus:outline-none focus:ring-2 focus:ring-white/50
            "
            aria-label="Previous slide"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 sm:h-5 sm:w-5 md:h-6 md:w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); nextSlide(); }}
            className="
              absolute right-2 sm:right-3 top-1/2 -translate-y-1/2 z-10
              bg-black/40 hover:bg-black/60 text-white
              p-1.5 sm:p-2 rounded-full
              transition-all duration-200
              opacity-70 sm:opacity-0 sm:group-hover:opacity-100
              focus:outline-none focus:ring-2 focus:ring-white/50
            "
            aria-label="Next slide"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 sm:h-5 sm:w-5 md:h-6 md:w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </>
      )}

      {/* Carousel Slides */}
      <div className="relative w-full aspect-[5/2] sm:aspect-[3/1] lg:aspect-auto lg:h-[280px] xl:h-[300px]">
        {bannerList.map((item: BannersItemModel, index) => (
          <Link
            key={item._id}
            to={getBannerUrl(item)}
            className="absolute top-0 left-0 w-full h-full cursor-pointer block"
            style={getItemStyle(index)}
          >
            <Image
              src={item.bannerImg}
              alt={item.title}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent flex items-end">
              <div className="w-full px-4 sm:px-6 md:px-8 pb-6 sm:pb-8 md:pb-10">
                <h1 className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold text-white line-clamp-2 max-w-[85%] sm:max-w-[70%] md:max-w-lg">
                  {item.title}
                </h1>
                {item?.description && (
                  <p className="text-gray-200 text-xs sm:text-sm md:text-base mt-1.5 sm:mt-2 line-clamp-2 max-w-[90%] sm:max-w-[60%] md:max-w-md">
                    {item.description}
                  </p>
                )}
              </div>
            </div>
          </Link>
        ))}
      </div>

      {/* Indicators */}
      {bannerList.length > 1 && (
        <div className="absolute bottom-2.5 sm:bottom-4 left-0 right-0 flex justify-center gap-1.5 sm:gap-2 z-10">
          {bannerList.map((_, index) => (
            <button
              key={index}
              onClick={(e) => { e.stopPropagation(); setCurrentIndex(index); }}
              className={`h-1.5 sm:h-2 rounded-full transition-all duration-300 ${
                currentIndex === index
                  ? 'bg-white w-5 sm:w-6'
                  : 'bg-white/50 w-1.5 sm:w-2'
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}
